import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit, formatTimeRemaining } from "@/hooks/use-rate-limit";
import { Mail, Phone, Globe, Loader2, Lock, Radar, Clock } from "lucide-react";
import { Helmet } from "@/components/Helmet";
import { AltchaWidget } from "@/components/AltchaWidget";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import type { Finding, ReportSummary, ScanResult } from "@/pages/portal/autoscan/types/customer";
import { LiveScanStatusPanel } from "@/components/live-scan/LiveScanStatusPanel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import {
  extractFindings,
  extractScanResults,
  extractSummary,
  getPublicScanApiBase,
  isValidDomain,
  isValidEmail,
  normalizeDomain,
  parseScanStatus,
} from "@/lib/live-scan";

const PUBLIC_SCAN_API_BASE = getPublicScanApiBase();

// Rate limit: default 5 live scans per hour (conservative), 5 contact submissions per hour
const LIVE_SCAN_MAX_ATTEMPTS = Number(import.meta.env.VITE_LIVE_SCAN_MAX_PER_HOUR || 5);
const LIVE_SCAN_RATE_LIMIT = {
  maxAttempts: Number.isFinite(LIVE_SCAN_MAX_ATTEMPTS) ? LIVE_SCAN_MAX_ATTEMPTS : 999,
  windowMs: 60 * 60 * 1000,
};
const CONTACT_RATE_LIMIT = { maxAttempts: 5, windowMs: 60 * 60 * 1000 };

const getTurnstileSiteKey = (): string | undefined => {
  const fromEnv =
    (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ||
    (import.meta.env.VITE_PUBLIC_TURNSTILE_SITE_KEY as string | undefined);
  if (fromEnv) return fromEnv;
  if (typeof document === "undefined") return undefined;

  const metaKey = document.querySelector<HTMLMetaElement>(
    'meta[name="turnstile-site-key"]',
  );
  const metaPublicKey = document.querySelector<HTMLMetaElement>(
    'meta[name="turnstile-site-key-public"]',
  );
  const key = metaKey?.content?.trim() || metaPublicKey?.content?.trim();
  if (!key) return undefined;
  if (key === "undefined") return undefined;
  if (key.includes("%VITE_") || key.includes("TURNSTILE_SITE_KEY")) return undefined;
  return key;
};

const mapLiveScanError = (code?: string, retryAfterSeconds?: number): string => {
  const retrySuffix =
    typeof retryAfterSeconds === "number" && Number.isFinite(retryAfterSeconds)
      ? ` Please retry in ${formatTimeRemaining(retryAfterSeconds * 1000)}.`
      : "";
  switch ((code || "").toLowerCase()) {
    case "rate_limited":
      return `You've hit the scan limit (5 per hour). Please try again later.${retrySuffix}`;
    case "rate_limited_concurrency":
      return `Only 1 scan can run at a time from your network.${retrySuffix}`;
    case "busy":
      return `Our scanners are currently busy. Please try again shortly.${retrySuffix}`;
    case "turnstile_failed":
    case "missing_turnstile_token":
      return "Verification failed. Please refresh the page and try again.";
    case "turnstile_not_configured":
      return "Live scan verification is unavailable right now. Please try again later.";
    case "invalid_url":
    case "invalid_hostname":
      return "Please enter a valid public website domain.";
    default:
      return "Unable to start the live scan. Please try again.";
  }
};

const Contact = () => {
  const { toast } = useToast();
  const [turnstileSiteKey] = useState(() => getTurnstileSiteKey());
  const hasTurnstile = Boolean(turnstileSiteKey);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [altchaToken, setAltchaToken] = useState<string | null>(null);
  const [liveScanTurnstileToken, setLiveScanTurnstileToken] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("live-scan");
  const [liveScanEmail, setLiveScanEmail] = useState("");
  const [liveScanDomain, setLiveScanDomain] = useState("");
  const [isLiveScanSubmitting, setIsLiveScanSubmitting] = useState(false);
  const [scanJobId, setScanJobId] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<"idle" | "starting" | "running" | "completed" | "error">("idle");
  const [scanSummary, setScanSummary] = useState<ReportSummary | null>(null);
  const [, setScanFindings] = useState<Finding[]>([]);
  const [, setScanResults] = useState<ScanResult[]>([]);
  const [scanLog, setScanLog] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanToken, setScanToken] = useState<string | null>(null);
  const [scanTargetUrl, setScanTargetUrl] = useState<string | null>(null);
  const [sseFailed, setSseFailed] = useState(false);
  const [pdfSending, setPdfSending] = useState(false);
  const [pdfSent, setPdfSent] = useState(false);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const [isLiveScanModalOpen, setIsLiveScanModalOpen] = useState(false);
  const [liveScanAuthorized, setLiveScanAuthorized] = useState(false);
  const hasTurnstileToken = Boolean(liveScanTurnstileToken);
  const logInlineRef = useRef<HTMLPreElement | null>(null);
  const logModalRef = useRef<HTMLPreElement | null>(null);
  const completionHandledRef = useRef<string | null>(null);

  // Rate limiting hooks
  const liveScanRateLimit = useRateLimit({ key: "live_scan", ...LIVE_SCAN_RATE_LIMIT });
  const contactRateLimit = useRateLimit({ key: "contact_form", ...CONTACT_RATE_LIMIT });

  const normalizedDomain = useMemo(() => normalizeDomain(liveScanDomain), [liveScanDomain]);

  useEffect(() => {
    const inlineEl = logInlineRef.current;
    if (inlineEl) inlineEl.scrollTop = inlineEl.scrollHeight;
    const modalEl = logModalRef.current;
    if (modalEl) modalEl.scrollTop = modalEl.scrollHeight;
  }, [scanLog]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Check rate limit before proceeding
    if (!contactRateLimit.checkLimit()) {
      toast({
        title: "Too many requests",
        description: `Please wait ${formatTimeRemaining(contactRateLimit.timeUntilReset)} before submitting again.`,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData(e.currentTarget);
      const data = { name: formData.get("name") as string, email: formData.get("email") as string, website: formData.get("website") as string, message: formData.get("message") as string, altchaToken };
      if (!altchaToken) {
        toast({ title: "Please complete the verification.", variant: "destructive" });
        setIsSubmitting(false);
        return;
      }
      const { error } = await supabase.functions.invoke("send-contact-email", { body: data });
      if (error) {
        // Check for rate limit response from server
        if (error.message?.includes("Too many requests") || error.message?.includes("429")) {
          toast({
            title: "Too many requests",
            description: "Please wait before submitting again.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }
      // Record successful attempt
      contactRateLimit.recordAttempt();
      toast({ title: "Request Received!", description: "We'll get back to you within 24 hours." });
      (e.target as HTMLFormElement).reset();
      setAltchaToken(null);
    } catch (error) {
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };


  const finalizeScan = useCallback(async (payload: unknown) => {
    if (!scanJobId || completionHandledRef.current === scanJobId) return;
    completionHandledRef.current = scanJobId;
    const findings = extractFindings(payload);
    const summary = extractSummary(payload, findings);
    const results = extractScanResults(payload);
    setScanFindings(findings);
    setScanSummary(summary);
    setScanResults(results);
    setScanStatus("completed");
    setScanError(null);
    setPdfAvailable(true);

    if (!liveScanEmail || !normalizedDomain) return;
    try {
      setPdfSending(true);
      const tokenParam = scanToken ? `?token=${encodeURIComponent(scanToken)}` : "";
      const res = await fetch(`${PUBLIC_SCAN_API_BASE}/scan/${scanJobId}/email${tokenParam}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: liveScanEmail }),
      });
      if (!res.ok) {
        throw new Error("Failed to send report email");
      }
      setPdfSent(true);
      toast({
        title: "Report emailed",
        description: "We sent your PDF report to your inbox and our team.",
      });
    } catch (error) {
      console.error("Failed to email report:", error);
      toast({
        title: "Report email failed",
        description: "The report is ready to download, but email delivery failed.",
        variant: "destructive",
      });
    } finally {
      setPdfSending(false);
    }
  }, [liveScanEmail, normalizedDomain, scanJobId, toast]);

  const fetchScanStatus = useCallback(async () => {
    if (!scanJobId) return;
    const tokenParam = scanToken ? `?token=${encodeURIComponent(scanToken)}` : "";
    const res = await fetch(`${PUBLIC_SCAN_API_BASE}/scan/${scanJobId}${tokenParam}`);
    if (!res.ok) {
      throw new Error("Failed to fetch scan status");
    }
    const payload = await res.json();
    const job = (payload as { job?: { targetUrl?: string } }).job;
    if (job?.targetUrl) setScanTargetUrl(job.targetUrl);
    const status = parseScanStatus(payload);
    setScanStatus(status);
    if (status === "completed") {
      await finalizeScan(payload);
    }
    if (status === "error") {
      setScanError("Scan failed. Please try again or contact support.");
    }
  }, [finalizeScan, scanJobId, scanToken]);

  useEffect(() => {
    if (!scanJobId || scanStatus === "completed" || scanStatus === "error") return;
    const tokenParam = scanToken ? `?token=${encodeURIComponent(scanToken)}` : "";
    const url = `${PUBLIC_SCAN_API_BASE}/scan/${scanJobId}/events${tokenParam}`;
    const es = new EventSource(url);
    setSseFailed(false);
    const handleLogChunk = (raw: string) => {
      try {
        const parsed = JSON.parse(raw);
        const line = String(parsed?.line || parsed?.chunk || parsed?.message || parsed?.log || "");
        if (line) setScanLog((prev) => `${prev}${line}\n`);
        const initLines = Array.isArray(parsed?.lines) ? parsed.lines : [];
        if (initLines.length > 0) {
          const text = initLines.map((item) => String(item?.line || "")).filter(Boolean).join("\n");
          if (text) setScanLog((prev) => `${prev}${text}\n`);
        }
      } catch {
        if (raw) setScanLog((prev) => `${prev}${raw}\n`);
      }
    };
    es.onmessage = (event) => {
      handleLogChunk(event.data);
    };
    es.addEventListener("log", (event) => {
      handleLogChunk((event as MessageEvent).data);
    });
    es.addEventListener("init", (event) => {
      handleLogChunk((event as MessageEvent).data);
    });
    es.addEventListener("done", () => {
      fetchScanStatus().catch(() => {});
    });
    es.onerror = () => {
      es.close();
      setSseFailed(true);
    };
    return () => {
      es.close();
    };
  }, [fetchScanStatus, scanJobId, scanStatus, scanToken]);

  useEffect(() => {
    if (!sseFailed || !scanJobId) return;
    if (scanStatus === "completed" || scanStatus === "error") return;
    const interval = setInterval(() => {
      fetchScanStatus().catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchScanStatus, scanJobId, scanStatus, sseFailed]);

  const handleLiveScanSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!liveScanEmail || !normalizedDomain) {
      toast({ title: "Missing info", description: "Enter an email and website domain.", variant: "destructive" });
      return;
    }

    // Client-side validation (matches server-side validation for defense in depth)
    if (!isValidDomain(normalizedDomain)) {
      toast({ 
        title: "Invalid domain", 
        description: "Please enter a valid domain name (e.g., example.com).", 
        variant: "destructive" 
      });
      return;
    }

    if (!isValidEmail(liveScanEmail)) {
      toast({ 
        title: "Invalid email", 
        description: "Please enter a valid email address.", 
        variant: "destructive" 
      });
      return;
    }

    // Check Turnstile verification when enabled
    if (hasTurnstile && !hasTurnstileToken) {
      toast({
        title: "Verification unavailable",
        description: "Turnstile is not ready. Please refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }

    // Check rate limit before proceeding
    if (!liveScanRateLimit.checkLimit()) {
      toast({
        title: "Too many scan requests",
        description: `You've reached the limit of ${LIVE_SCAN_RATE_LIMIT.maxAttempts} scans per hour. Please wait ${formatTimeRemaining(liveScanRateLimit.timeUntilReset)} before scanning again.`,
        variant: "destructive",
      });
      return;
    }
    if (!liveScanAuthorized) {
      toast({
        title: "Authorisation required",
        description: "Confirm you own or are authorised to scan this site to continue.",
        variant: "destructive",
      });
      return;
    }

    setIsLiveScanSubmitting(true);
    setScanStatus("starting");
    setScanJobId(null);
    setScanError(null);
    setScanToken(null);
    setScanTargetUrl(null);
    setPdfSent(false);
    setPdfAvailable(false);
    setScanSummary(null);
    setScanFindings([]);
    setScanResults([]);
    setScanLog("");
    completionHandledRef.current = null;

    try {
      const res = await fetch(`${PUBLIC_SCAN_API_BASE}/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain: normalizedDomain,
          email: liveScanEmail,
          turnstileToken: liveScanTurnstileToken,
        }),
      });

      if (!res.ok) {
        let errorMessage = "";
        let errorCode = "";
        let retryAfterSeconds: number | undefined;
        let data:
          | { error?: string; message?: string; code?: string; retryAfterSeconds?: number }
          | undefined;
        try {
          data = await res.json();
          errorMessage = String(data?.error || data?.message || "");
          errorCode = String(data?.code || data?.error || "");
          retryAfterSeconds = data?.retryAfterSeconds;
        } catch {
          errorMessage = "";
        }

        console.warn("[LiveScan] start failed", {
          status: res.status,
          error: data?.error,
          code: data?.code,
          body: data,
        });

        if (res.status === 429 || errorMessage.includes("Too many")) {
          const message = mapLiveScanError(errorCode || "rate_limited", retryAfterSeconds);
          toast({
            title: "Scan limit reached",
            description: message,
            variant: "destructive",
          });
          setScanStatus("error");
          setScanError(message);
          return;
        }

        throw new Error(data?.error || errorMessage || "Failed to start live scan");
      }

      const data = await res.json();
      if (data?.error) {
        console.warn("[LiveScan] start failed", {
          status: res.status,
          error: data?.error,
          body: data,
        });
        throw new Error(data.error);
      }

      const jobId = String(data?.jobId || "");
      const token = String(data?.token || "");
      if (!jobId) throw new Error("No job ID returned from scan API");
      
      // Record successful scan attempt on client side too
      liveScanRateLimit.recordAttempt();
      
      // Reset Turnstile token after successful submission
      setLiveScanTurnstileToken(null);
      
      setScanJobId(jobId);
      setScanToken(token || null);
      setScanStatus("running");
      setIsLiveScanModalOpen(true);
      toast({
        title: "Live scan started",
        description: "We just emailed you a private link to track progress.",
      });
    } catch (error) {
      console.warn("[LiveScan] start failed", { error: String(error) });
      const message = mapLiveScanError(
        error instanceof Error ? error.message : undefined,
      );
      setScanStatus("error");
      setScanError(message);
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setIsLiveScanSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!scanJobId) return;
    const tokenParam = scanToken ? `?token=${encodeURIComponent(scanToken)}` : "";
    if (scanTargetUrl) {
      const domain = normalizeDomain(scanTargetUrl);
      window.open(`/live-scan/${scanJobId}/${domain}.pdf${tokenParam}`, "_blank");
      return;
    }
    window.open(`${PUBLIC_SCAN_API_BASE}/scan/${scanJobId}/report.pdf${tokenParam}`, "_blank");
  };

  const statusPageHref = scanJobId
    ? `/live-scan/${scanJobId}${scanToken ? `?token=${encodeURIComponent(scanToken)}` : ""}`
    : undefined;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Request a Security Assessment | ShadowScan</title>
        <meta
          name="description"
          content="Request a WordPress security assessment from ShadowScan and talk with real security experts about risk, remediation, and managed monitoring."
        />
        <meta name="referrer" content="no-referrer" />
        <meta httpEquiv="Cache-Control" content="no-store" />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// REQUEST_SCAN</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">Request Your <span className="text-gradient">Security Assessment</span></h1>
              <p className="text-lg text-muted-foreground">Get a clear assessment of risk and remediation priorities for your WordPress site</p>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
              <div className="cyber-card rounded-xl p-8 border-primary/30">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                  <TabsList className="grid grid-cols-2 w-full bg-secondary/40">
                    <TabsTrigger value="live-scan" className="font-mono text-xs">Live Scan</TabsTrigger>
                    <TabsTrigger value="contact" className="font-mono text-xs">Contact Us</TabsTrigger>
                  </TabsList>

                  <TabsContent value="live-scan" className="space-y-6">
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                      <span className="font-semibold text-amber-200">Alpha notice:</span> Live Scan is in active testing. Expect changes and occasional instability.
                    </div>
                    <form onSubmit={handleLiveScanSubmit} className="space-y-5">
                      <div>
                        <Label htmlFor="live-email" className="text-sm font-mono text-muted-foreground">EMAIL *</Label>
                        <Input
                          id="live-email"
                          name="live-email"
                          type="email"
                          placeholder="your@email.com"
                          required
                          className="mt-2 bg-secondary/50"
                          value={liveScanEmail}
                          onChange={(e) => setLiveScanEmail(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="live-domain" className="text-sm font-mono text-muted-foreground">WEBSITE DOMAIN *</Label>
                        <Input
                          id="live-domain"
                          name="live-domain"
                          placeholder="example.com"
                          required
                          className="mt-2 bg-secondary/50"
                          value={liveScanDomain}
                          onChange={(e) => setLiveScanDomain(e.target.value)}
                        />
                      </div>
                      {hasTurnstile ? (
                        <div className="rounded-md border border-border/50 p-3 bg-muted/30">
                          <p className="text-xs text-muted-foreground mb-2">Please verify to continue</p>
                          <TurnstileWidget
                            siteKey={turnstileSiteKey}
                            onToken={setLiveScanTurnstileToken}
                            className="block"
                          />
                        </div>
                      ) : (
                        <div className="rounded-md border border-destructive/50 p-3 bg-destructive/10">
                          <p className="text-xs text-destructive font-medium mb-1">
                            Verification is unavailable
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Our dev team are currently on it! Thank you for your patience.
                          </p>
                        </div>
                      )}
                      <div className="rounded-md border border-border/50 p-3 bg-muted/30 text-xs text-muted-foreground">
                        <div className="flex items-start gap-2">
                          <Checkbox
                            id="live-scan-authorised"
                            checked={liveScanAuthorized}
                            onCheckedChange={(checked) => setLiveScanAuthorized(checked === true)}
                            className="mt-0.5"
                          />
                          <label htmlFor="live-scan-authorised" className="leading-5">
                            I confirm I own this site or have explicit authorisation to run this scan. I understand this is a non-intrusive assessment and agree to the ShadowScan{" "}
                            <Link className="text-primary underline hover:no-underline" to="/terms">
                              Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link className="text-primary underline hover:no-underline" to="/privacy">
                              Privacy Policy
                            </Link>
                            .
                          </label>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        size="lg"
                        className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
                        disabled={
                          isLiveScanSubmitting ||
                          scanStatus === "running" ||
                          scanStatus === "starting" ||
                          liveScanRateLimit.isLimited ||
                          (hasTurnstile && !hasTurnstileToken) ||
                          !liveScanAuthorized
                        }
                      >
                        {liveScanRateLimit.isLimited ? (
                          <>
                            <Clock className="mr-2 h-5 w-5" />
                            Wait {formatTimeRemaining(liveScanRateLimit.timeUntilReset)}
                          </>
                        ) : isLiveScanSubmitting || scanStatus === "starting" ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <Radar className="mr-2 h-5 w-5" />Start Live Scan
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        {liveScanRateLimit.isLimited 
                          ? `Rate limited: ${liveScanRateLimit.remainingAttempts} of ${LIVE_SCAN_RATE_LIMIT.maxAttempts} scans remaining this hour.`
                          : `We'll email your private scan link now, plus the report when it finishes. (${liveScanRateLimit.remainingAttempts} scans remaining this hour)`
                        }
                      </p>
                    </form>

                    {scanStatus !== "idle" && (
                      <LiveScanStatusPanel
                        status={scanStatus}
                        scanLog={scanLog}
                        scanSummary={scanSummary}
                        scanError={scanError}
                        pdfAvailable={pdfAvailable}
                        pdfSending={pdfSending}
                        pdfSent={pdfSent}
                        onDownloadPdf={handleDownloadPdf}
                        logRef={logInlineRef}
                        statusPageHref={statusPageHref}
                        showEmailStatus
                      />
                    )}
                  </TabsContent>

                  <TabsContent value="contact" className="space-y-5">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div><Label htmlFor="name" className="text-sm font-mono text-muted-foreground">NAME *</Label><Input id="name" name="name" placeholder="Your name" required className="mt-2 bg-secondary/50" /></div>
                      <div><Label htmlFor="email" className="text-sm font-mono text-muted-foreground">EMAIL *</Label><Input id="email" name="email" type="email" placeholder="your@email.com" required className="mt-2 bg-secondary/50" /></div>
                      <div><Label htmlFor="website" className="text-sm font-mono text-muted-foreground">WEBSITE *</Label><Input id="website" name="website" placeholder="https://yoursite.com" required className="mt-2 bg-secondary/50" /></div>
                      <div><Label htmlFor="message" className="text-sm font-mono text-muted-foreground">MESSAGE</Label><Textarea id="message" name="message" placeholder="Tell us about your security concerns..." rows={4} className="mt-2 bg-secondary/50" /></div>
                      <div className="rounded-md border border-border/50 p-3 bg-muted/30">
                        <p className="text-xs text-muted-foreground mb-2">Please verify to submit</p>
                        <AltchaWidget onToken={setAltchaToken} className="block" />
                      </div>
                      <Button type="submit" size="lg" className="w-full bg-accent hover:bg-accent/90 text-accent-foreground" disabled={isSubmitting || contactRateLimit.isLimited}>
                        {contactRateLimit.isLimited ? (
                          <>
                            <Clock className="mr-2 h-5 w-5" />
                            Wait {formatTimeRemaining(contactRateLimit.timeUntilReset)}
                          </>
                        ) : isSubmitting ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <>
                            <Lock className="mr-2 h-5 w-5" />Submit Request
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        {contactRateLimit.isLimited 
                          ? `Rate limited: Please wait before submitting again.`
                          : `We'll respond within 24 hours`
                        }
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>

              <div className="space-y-8">
                <div><div className="font-mono text-sm text-primary mb-2">// GET_IN_TOUCH</div><h2 className="text-2xl font-heading font-bold mb-4">Contact Us</h2><p className="text-muted-foreground">Have questions? We're here to help.</p></div>
                <div className="space-y-6">
                  {[{ icon: Mail, label: "Email", value: "support@shadowscan.com.au", href: "mailto:support@shadowscan.com.au" }, { icon: Phone, label: "Phone", value: "0480 465 208", href: "tel:0480465208" }, { icon: Globe, label: "Location", value: "Serving WordPress sites worldwide" }].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center"><item.icon className="h-5 w-5 text-primary" /></div>
                      <div><h3 className="font-semibold text-sm">{item.label}</h3>{item.href ? <a href={item.href} className="text-muted-foreground hover:text-primary text-sm">{item.value}</a> : <p className="text-muted-foreground text-sm">{item.value}</p>}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Dialog open={isLiveScanModalOpen} onOpenChange={setIsLiveScanModalOpen}>
        <DialogContent className="max-w-3xl cyber-card">
          <DialogHeader>
            <DialogTitle className="font-mono">Live Scan Status</DialogTitle>
            <DialogDescription>
              Track progress and download your report. We emailed you a private status link.
            </DialogDescription>
          </DialogHeader>
          {scanStatus !== "idle" && (
            <LiveScanStatusPanel
              status={scanStatus}
              scanLog={scanLog}
              scanSummary={scanSummary}
              scanError={scanError}
              pdfAvailable={pdfAvailable}
              pdfSending={pdfSending}
              pdfSent={pdfSent}
              onDownloadPdf={handleDownloadPdf}
              logRef={logModalRef}
              statusPageHref={statusPageHref}
              showEmailStatus
            />
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default Contact;
